import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';
import { ApiListTransitionStoryGroupsResponse, ApiTransitionElementResponse } from '../../api/core/models';
import { createNodesAndIndicators } from '../../public/dashboard/shared/functions/dashboard-mappers/create-nodes-and-indicators';
import { BackendPublishedBoardService } from '../../services/backend/backend-published-board.service';
import { PublishedBoardsModule } from './published-boards-modules';
import { publishedBoardEffectsActions, publishedBoardsFacadeActions } from './published-boards.actions';
import { selectPublishedBoardsState } from './published-boards.selectors';
import { PublishedBoardState } from './published-boards.types';

export const loadPublishedBoard = createEffect(
  (
    actions$ = inject(Actions),
    backendPublishedBoardService = inject(BackendPublishedBoardService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(publishedBoardsFacadeActions.loadPublishedBoard),
      concatLatestFrom(() => [store.select(selectPublishedBoardsState)]),
      exhaustMap(([{ boardId, forceLoad }, publishedBoards]) => {
        if (publishedBoards[boardId] && !forceLoad) {
          return of(publishedBoardEffectsActions.noChanges());
        }
        return backendPublishedBoardService.getPublishedBoard(boardId).pipe(
          map(({ id, content, createdAt, status, version }) => {
            return publishedBoardEffectsActions.setPublishedBoard({
              publishedBoard: {
                boardId,
                date: createdAt,
                version,
                versionId: id,
                status,
                oldBlobUrl: content.oldBlobUrl,
                modules: content.modules,
                moduleData: {},
              },
            });
          }),
          catchError((error) => {
            return of(publishedBoardEffectsActions.error({ boardId, error }));
          })
        );
      })
    );
  },
  { functional: true }
);

export const loadPublishedBoardData = createEffect(
  (
    actions$ = inject(Actions),
    backendPublishedBoardService = inject(BackendPublishedBoardService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(publishedBoardsFacadeActions.loadPublishedBoardData),
      concatLatestFrom(() => [store.select(selectPublishedBoardsState)]),
      mergeMap(([{ boardId, module }, publishedBoards]) => {
        // if the version is 5 or larger, we fetch only the module provided
        const publishedVersion = Number((<PublishedBoardState>publishedBoards[boardId])?.version.replace('V', '')) || 1;
        if (publishedVersion >= 5) {
          if (
            (<PublishedBoardState>publishedBoards[boardId])?.moduleData !== undefined &&
            (<PublishedBoardState>publishedBoards[boardId]).moduleData[module!]
          ) {
            return of(publishedBoardEffectsActions.noChanges());
          } else {
            const moduleToFetch = (<PublishedBoardState>publishedBoards[boardId])?.modules?.find(
              (m) => m.type === module
            );
            if (!moduleToFetch) {
              if (module === PublishedBoardsModule.SEARCH) {
                return of(
                  publishedBoardEffectsActions.setPublishedBoardModuleData({
                    boardId,
                    module: module!,
                    moduleData: [],
                  })
                );
              }
              return of(publishedBoardEffectsActions.error({ boardId, error: new Error('Module not found') }));
            }
            return backendPublishedBoardService.getPublishedBoardData(moduleToFetch.blobUrl!).pipe(
              map((data) => {
                if (publishedVersion > 5 && module === PublishedBoardsModule.TRANSITIONS) {
                  const { nodes, indicators, supplyElements, childNodes } = createNodesAndIndicators(
                    (data as unknown as { supplyTransitionElements: ApiTransitionElementResponse[] })
                      .supplyTransitionElements,
                    (data as unknown as ApiListTransitionStoryGroupsResponse)?.transitionStoryGroups ?? [],
                    boardId
                  );
                  return publishedBoardEffectsActions.setPublishedBoardModuleData({
                    boardId,
                    module: module!,
                    moduleData: { nodes: nodes.concat(supplyElements), indicators, supplyElements, childNodes },
                  });
                }
                return publishedBoardEffectsActions.setPublishedBoardModuleData({
                  boardId,
                  module: module!,
                  moduleData: data,
                });
              })
            );
          }
        }

        // if the published board not yet exists in the store, we fetch it before fetching the data
        if (!publishedBoards[boardId] || publishedBoards[boardId] instanceof Error) {
          return backendPublishedBoardService.getPublishedBoard(boardId).pipe(
            switchMap(({ id, content, createdAt, status, version }) => {
              return backendPublishedBoardService.getPublishedBoardData(content.oldBlobUrl!).pipe(
                map((data) => {
                  return publishedBoardEffectsActions.setPublishedBoard({
                    publishedBoard: {
                      oldBlobUrl: content.oldBlobUrl,
                      boardId,
                      data,
                      date: createdAt,
                      version,
                      versionId: id,
                      status,
                      moduleData: {},
                      modules: [],
                    },
                  });
                })
              );
            }),
            catchError((error) => {
              return of(publishedBoardEffectsActions.error({ boardId, error }));
            })
          );
        }

        return backendPublishedBoardService
          .getPublishedBoardData((<PublishedBoardState>publishedBoards[boardId]).oldBlobUrl!)
          .pipe(
            map((data) => {
              return publishedBoardEffectsActions.setPublishedBoardData({ boardId, publishedBoardData: data });
            }),
            catchError((error) => {
              return of(publishedBoardEffectsActions.error({ boardId, error }));
            })
          );
      })
    );
  },
  { functional: true }
);
